import { GAME, ITEM_TYPES } from '../config.js';
import { getDifficultySettings, getSpawnInterval } from './difficulty.js';
import { getEnrichmentPace, hasEnrichmentProgress } from './enrichmentPace.js';
import { createItem } from './itemFactory.js';

export function chooseItemType({
    settings,
    recentTypes = [],
    random = Math.random,
    elapsedSeconds = 0,
}) {
    if (GAME.ACTIVITIES_ONLY) {
        const lastTwo = recentTypes.slice(-2);
        const lastTwoNegative =
            lastTwo.length === 2 && lastTwo.every((type) => type === ITEM_TYPES.BAD_ITEM);

        const roll = random();

        if (!lastTwoNegative && roll < settings.badChance) {
            return ITEM_TYPES.BAD_ITEM;
        }

        return ITEM_TYPES.ACTIVITY_ITEM;
    }

    // The first few seconds are deliberately friendly.
    if (elapsedSeconds < 3) {
        return random() < 0.86 ? ITEM_TYPES.GOOD_LEAD : ITEM_TYPES.HOT_LEAD;
    }

    const lastTwo = recentTypes.slice(-2);
    const lastTwoNegative =
        lastTwo.length === 2 && lastTwo.every((type) => type === ITEM_TYPES.BAD_ITEM);

    const roll = random();

    if (!lastTwoNegative && roll < settings.badChance) {
        return ITEM_TYPES.BAD_ITEM;
    }

    const activityChance = settings.activityChance ?? 0;

    if (roll < settings.badChance + activityChance) {
        return ITEM_TYPES.ACTIVITY_ITEM;
    }

    if (roll < settings.badChance + activityChance + settings.bonusChance) {
        return ITEM_TYPES.BONUS;
    }

    if (
        roll <
        settings.badChance + activityChance + settings.bonusChance + settings.hotLeadChance
    ) {
        return ITEM_TYPES.HOT_LEAD;
    }

    return ITEM_TYPES.GOOD_LEAD;
}

export function createNextSpawn({
    difficulty = 'normal',
    state,
    boardSize,
    nowMs,
    random = Math.random,
    enrichmentProgress = null,
}) {
    const baseSettings = getDifficultySettings(difficulty);
    const enrichmentPace = hasEnrichmentProgress(enrichmentProgress)
        ? getEnrichmentPace(enrichmentProgress, baseSettings)
        : null;
    const settings = enrichmentPace
        ? { ...baseSettings, badChance: enrichmentPace.badChance }
        : baseSettings;

    const elapsedSeconds = enrichmentPace
        ? enrichmentPace.rampElapsedSeconds
        : Math.max(0, state.duration - state.timeLeft);
    const rampDuration = enrichmentPace ? GAME.SPAWN_RAMP_SECONDS : state.duration;

    const recentTypes = state.items.slice(-4).map((item) => item.type);

    let nextSpawnInMs;

    if (enrichmentPace && GAME.ACTIVITIES_ONLY) {
        const [minInterval, maxInterval] = settings.spawnIntervalMs;
        const base = minInterval * 0.68 + random() * (maxInterval - minInterval) * 0.68;
        nextSpawnInMs = Math.max(
            400,
            Math.round((base * GAME.ACTIVITY_SPAWN_INTERVAL_SCALE) / enrichmentPace.spawnIntensity),
        );
    } else {
        nextSpawnInMs = GAME.ACTIVITIES_ONLY
            ? Math.max(
                  320,
                  Math.round(
                      getSpawnInterval(settings, elapsedSeconds, rampDuration, random) *
                          GAME.ACTIVITY_SPAWN_INTERVAL_SCALE,
                  ),
              )
            : getSpawnInterval(settings, elapsedSeconds, rampDuration, random);
    }

    if (state.items.length >= GAME.MAX_ITEMS_ON_SCREEN) {
        return {
            item: null,
            nextSpawnInMs,
        };
    }

    const type = chooseItemType({ settings, recentTypes, random, elapsedSeconds });
    const recentPositions = state.items.slice(-5).map((item) => ({ x: item.x, y: item.y }));

    const item = createItem(type, {
        boardSize,
        lifetimeMs: settings.itemLifetimeMs,
        nowMs,
        random,
        recentPositions,
        stackIndex: state.items.length,
    });

    return {
        item,
        nextSpawnInMs,
    };
}
